import React, { useState, useEffect, useCallback } from 'react'
import { useForm, FormContext } from 'react-hook-form'

import {
  Box,
  Divider,
  Grid,
  Paper,
  Tab,
  Tabs,
  useMediaQuery,
} from '@material-ui/core'

import BannerHeadCard from '../BannerHeadCard'
import {
  BannerPreview,
  ColorCustomization,
  FontCustomization,
  LogoPreview,
  LogoUpload,
} from '../BannerCustomizationCard/components'

import useCookieBanner from 'hooks/useCookieBanner'
import useSnackbar from 'hooks/useSnackbar'

import { bannerAppearanceSchema } from '../../stepSchemas'
import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import styles from './styles'
import theme from 'theme'

const BannerAppearanceCard = () => {
  const formMethods = useForm({
    validationSchema: bannerAppearanceSchema,
    defaultValues: {
      primaryColor: theme.palette.primary.main,
      secondaryColor: theme.palette.primary.hover,
      fontFamily: '',
      logo: undefined,
      isCompact: false,
    },
  })
  const { handleSubmit, reset, watch } = formMethods
  const snackbar = useSnackbar()

  const {
    banner,
    refreshBanner,
    nextStep,
    setIsSubmitting,
    setStepSubmit,
    setAppearanceValues,
  } = useCookieBanner()

  const watchedPrimaryColor = watch('primaryColor')
  const watchedSecondaryColor = watch('secondaryColor')
  const watchedFontFamily = watch('fontFamily')

  useEffect(() => {
    setAppearanceValues({
      primaryColor: watchedPrimaryColor,
      secondaryColor: watchedSecondaryColor,
      fontFamily: watchedFontFamily,
    })
  }, [watchedPrimaryColor, watchedSecondaryColor, watchedFontFamily])

  const [openBanner, setOpenBanner] = useState(true)
  const [selectedTab, setSelectedTab] = useState(
    constants.cookies.banner.COLOR_CUSTOMIZATION_CARD_ID,
  )

  const useStyles = styles({ selectedTab })
  const classes = useStyles()

  const smDown = useMediaQuery(theme.breakpoints.down('sm'), {
    defaultMatches: true,
  })

  const logoPreview =
    selectedTab === constants.cookies.banner.LOGO_CUSTOMIZATION_CARD_ID

  const handleTabChange = (_, clickedTab) => setSelectedTab(clickedTab)

  const handleFormSubmit = useCallback(
    async (data) => {
      try {
        setIsSubmitting(true)

        const bannerData = {
          primaryColor: data.primaryColor,
          secondaryColor: data.secondaryColor,
          fontFamily: data.fontFamily,
          isCompact: data.isCompact,
        }

        if (data.logo) {
          await service.cookies.banner.uploadLogo({
            bannerId: banner?.id,
            logo: data.logo,
          })
        }

        await service.cookies.banner.updateAppearance({
          bannerId: banner?.id,
          banner: bannerData,
        })

        snackbar.open({
          variant: 'success',
          message:
            constants.cookies.banner.SUCCESS_MESSAGES[
              constants.cookies.banner.STEPS.APPEARANCE
            ],
        })

        await refreshBanner()
        nextStep()
      } catch (error) {
        snackbar.open({
          variant: 'error',
          message: 'Erro ao salvar aparência. Tente novamente.',
        })
      } finally {
        setIsSubmitting(false)
      }
    },
    [banner, setIsSubmitting, snackbar, refreshBanner, nextStep],
  )

  useEffect(() => {
    if (!banner) return

    reset({
      primaryColor: banner.primaryColor || theme.palette.primary.main,
      secondaryColor: banner.secondaryColor || theme.palette.primary.hover,
      fontFamily: banner.fontFamily || '',
      logo: undefined,
      isCompact: banner.isCompact || false,
    })
  }, [banner, reset])

  useEffect(() => {
    setStepSubmit(handleSubmit(handleFormSubmit))
  }, [handleSubmit, handleFormSubmit, setStepSubmit])

  const leftSizes = helpers.cookies.banner.computeLeftGridSizes({
    positionPreview: false,
    isTextCustomizationLevel2Preview: false,
    logoPreview,
  })
  const rightSizes = helpers.cookies.banner.computeRightGridSizes({
    positionPreview: false,
    isTextCustomizationLevel2Preview: false,
    logoPreview,
  })

  return (
    <FormContext {...formMethods}>
      <Paper
        variant="outlined"
        id={constants.cookies.banner.COOKIES_BANNER_SETTING_DRIVER_STEP_3}
      >
        <BannerHeadCard name="Aparência" />
        <Divider />
        <Box
          p={2}
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(2)}
        >
          <Tabs
            value={selectedTab}
            variant="scrollable"
            onChange={handleTabChange}
          >
            {constants.cookies.banner.APPEARANCE_CARD_TABS.map((tab) => (
              <Tab key={tab.id} label={tab.tabName} value={tab.id} />
            ))}
          </Tabs>
          <Grid container spacing={smDown ? 2 : 0}>
            <Grid item {...leftSizes}>
              <Box className={classes.colorCustomizationContainer}>
                <ColorCustomization />
              </Box>
              <Box className={classes.logoUploadContainer}>
                <LogoUpload />
              </Box>
              <Box className={classes.fontCustomizationContainer}>
                <FontCustomization />
              </Box>
            </Grid>
            <Grid item {...rightSizes}>
              <Box className={classes.bannerPreviewContainer}>
                <BannerPreview
                  openBanner={openBanner}
                  setOpenBanner={setOpenBanner}
                />
              </Box>
              <Box className={classes.logoPreviewContainer}>
                <LogoPreview banner={banner} />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </FormContext>
  )
}

export default BannerAppearanceCard
